import { apiErrorText } from "../../api/api-error.js";
import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * "Talk in the browser" — the browser voice test (plan-004 WC-51, FR-BV-1…14, design §17.2.1).
 *
 * ONE hook and two layouts, so the three places it appears cannot drift into three meanings:
 *
 *   * {@link BrowserTestCallPane} — the third direction of the voice test, beside "Agent calls me"
 *     and "I call the agent", on the Test console and the agent's Test tab (FR-BV-1 (b)): the controls
 *     on the left, the live transcript pane on the right, as the phone test lays them out;
 *   * the test panel's **Voice** mode on the agent's page (FR-BV-1 (a)), drawn by `test-chat-panel.tsx`
 *     from {@link useBrowserTestCall}, {@link BrowserVoiceControls} and {@link BrowserVoiceStatus}
 *     as the prototype draws it (`design/WeTalk.dc.html` 3728–3736: the microphone button, fourteen
 *     level bars, the duration, "Browser mic · idle · nothing billed"). The Versions tab's draft panel
 *     does not offer it (FR-BV-1 (c)).
 *
 * THE ORDER OF THINGS (design §17.2.1). The browser voice module is fetched when this mounts — its own
 * chunk, never the console's entry chunk (plan-004 R17) — so that Start can call `requestMicrophone()`
 * synchronously INSIDE the click, which is what browsers (iOS Safari first) require of a microphone
 * prompt. A refused microphone ends there: no api request, nothing written (AC-BV-7). A granted one
 * asks the api for the grant; a refusal stops the microphone again and says so in the api's words
 * ("… Nothing was charged."), with Try again when every line was busy (AC-BV-6). Otherwise the module
 * joins WeTalk's relay and reports connecting → live (timer, level meter) ⇄ queued / reconnecting →
 * ended, and the transcript streams in through the same SSE and the same stick-to-bottom hook as the
 * phone test (FR-BV-8, FR-SC-5). Hang up leaves the room and asks the api to end the test; the ended
 * line is read back from the api: duration · charged · list price with the trial note (#233, AC-BV-8).
 *
 * WHAT IS SAID BEFORE ANYTHING IS PRESSED (FR-BV-3, FR-BV-13, FR-BV-14): whether a browser test can run
 * now (the availability read — the model resting, the relay unreachable), who may start one (a Viewer
 * sees the reason and no button), that it is billed like a voice test from the moment the agent
 * answers with the free minutes first, and that starting one ends any browser test already running
 * for this person on this agent.
 *
 * AMENDED 2026-09-26 (plan-005 OP-37, design §18.14.3, IC-50): an order confirmed for online payment in
 * a browser test arrives on the same stream as `customer_order_payment`, and both layouts draw it as the
 * website widget's payment element, marked test, between the lines it came between.
 */
import { useQuery } from "@tanstack/react-query";
import { Button, Icon, Tag } from "@wetalk/ui";
// Types only: the module itself is reached through `gateway.loadBrowserVoiceClient()` alone.
import type { BrowserVoiceHandle, BrowserVoiceState } from "@wetalk/webrtc-client";
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

import { GLYPH } from "../../shell/glyph.js";
import { useMemberCan } from "../../shell/member-capability.js";
import { useGateway } from "../../api/provider.js";
import { TestCallUnavailableError, type BrowserTestCallView, type BrowserVoiceClient, type SurveyArmView } from "../../api/slice/test-call.js";
import type { TestCallAvailabilityView, TestCallView, TranscriptLineView } from "../../api/gateway.js";
import {
  countdownWordsOf,
  endedWordsOf,
  failedWordsOf,
  isConnected,
  isUnderWay,
  levelBarsOf,
  microphoneWordsOf,
  underWayWordsOf,
  type BrowserVoicePhase,
} from "./browser-voice-state.js";
import { endedLineOf } from "./ended-line.js";
import { PaymentElementCard } from "./payment-element-card.js";
import { CONFIRMATION_CODE_SHOWN_EVENT, consoleCodeOf } from "../../api/party-confirmation-view.js";
import { voiceAgentPassFrameOf, type VoiceAgentPassArrival } from "../../api/voice-agent-stint-view.js";
import {
  interleavedEntries,
  paymentElementEventOf,
  withPaymentEvent,
  type PaymentElementView,
} from "./payment-element.js";

/** How often the ended test is read back until the runtime has written its end. */
const ENDED_READ_INTERVAL_MS = 2_000;
/** How often the live timer and the countdown move. */
const TICK_MS = 1_000;

export interface BrowserTestCallControl {
  readonly phase: BrowserVoicePhase;
  /** The microphone's level, 0..1. */
  readonly level: number;
  /** The clock the timer and the countdown read (epoch ms). */
  readonly nowMs: number;
  /** The final-minute countdown's end (epoch ms), from the runtime's notice; `null` when not counting. */
  readonly finalMinuteUntilMs: number | null;
  /** Autoplay blocked the agent's audio: show "Tap to hear the agent". */
  readonly audioBlocked: boolean;
  readonly resumeAudio: () => void;
  /** Whether the browser voice module has loaded (Start waits for it), or failed to. */
  readonly moduleState: "loading" | "ready" | "failed";
  /** The api's answer to the start that is running or ran last; `null` before one. */
  readonly started: BrowserTestCallView | null;
  readonly conversationId: string | null;
  /** The live transcript, from the conversation's stream. */
  readonly line: readonly TranscriptLineView[];
  /**
   * Plan-005 OP-37 (IC-50): the payment elements the runtime published on the same stream
   * (`customer_order_payment`), each placed after the line it followed.
   */
  readonly payment: readonly PaymentElementView[];
  /** plan-021 (design §33.13.3): the passes heard on the same stream, each after the lines heard before it. */
  readonly voiceAgentPassArrival: readonly VoiceAgentPassArrival[];
  /**
   * plan-029 WAPP-HIST2 (design §40.19.5, ED-17): this test's latest confirmation code, shown instead of being sent
   * (`confirmation_code_shown`, relayed to the tester only); `null` before one. Never stored.
   */
  readonly consoleCode: string | null;
  /** The ended test as the api reads it back — `state: ended` carries what was charged. */
  readonly endedView: TestCallView | null;
  /** A Hang up that the api could not confirm, in words. */
  readonly hangUpNote: string | null;
  /** Must be called from the click handler: it asks for the microphone synchronously. */
  readonly start: () => void;
  readonly hangUp: () => void;
}

/** A refused start, in the tester's words. */
function refusedPhaseOf(error: unknown): BrowserVoicePhase {
  if (error instanceof TestCallUnavailableError) {
    return { kind: "refused", message: apiErrorText(error, ""), neutral: true, retry: error.code === "all_lines_busy" };
  }
  const message = error instanceof Error ? apiErrorText(error, "") : portalText("operations", "the_test_could_not_start_nothing_was_charged_56e27f2e");
  return { kind: "refused", message, neutral: false, retry: false };
}

function phaseOfModuleState(state: BrowserVoiceState, endedHere: boolean): BrowserVoicePhase {
  switch (state.kind) {
    case "connecting":
      return { kind: "connecting" };
    case "live":
      return { kind: "live", sinceMs: state.sinceMs };
    case "queued":
      return { kind: "queued" };
    case "reconnecting":
      return { kind: "reconnecting" };
    case "ended":
      return { kind: "ended", reason: state.reason, endedHere };
    case "failed":
      return { kind: "failed", reason: state.reason };
  }
}

/** A granted microphone's stream, as far as releasing it goes. */
interface GrantedMicrophone {
  getTracks(): readonly { stop(): void }[];
}

function stopTracks(stream: GrantedMicrophone): void {
  for (const track of stream.getTracks()) track.stop();
}

/** A transcript frame, added to the lines: a partial replaces the same speaker's partial before it. */
function withFrame(held: readonly TranscriptLineView[], next: TranscriptLineView): readonly TranscriptLineView[] {
  const last = held.at(-1);
  if (last !== undefined && last.partial && last.speaker === next.speaker) return [...held.slice(0, -1), next];
  return [...held, next];
}

/**
 * `surveyArm` (plan-015 TC2, G21): a two-group survey's question group ("Test group 1" = `A`, "group 2"
 * = `B`), sent with the start; the panel locks the choice while a test is under way.
 */
export function useBrowserTestCall(voiceAgentId: string, surveyArm?: SurveyArmView): BrowserTestCallControl {
  const gateway = useGateway();
  const [phase, setPhase] = useState<BrowserVoicePhase>({ kind: "idle" });
  const [level, setLevel] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [finalMinuteUntilMs, setFinalMinuteUntilMs] = useState<number | null>(null);
  const [resume, setResume] = useState<(() => Promise<void>) | null>(null);
  const [moduleState, setModuleState] = useState<"loading" | "ready" | "failed">("loading");
  const [started, setStarted] = useState<BrowserTestCallView | null>(null);
  const [line, setLine] = useState<readonly TranscriptLineView[]>([]);
  const [payment, setPayment] = useState<readonly PaymentElementView[]>([]);
  const [voiceAgentPassArrival, setVoiceAgentPassArrival] = useState<readonly VoiceAgentPassArrival[]>([]);
  const [consoleCode, setConsoleCode] = useState<string | null>(null);
  /** The line count the stream handler sees, so an element is placed after the line it followed. */
  const lineCount = useRef(0);
  const [hangUpNote, setHangUpNote] = usePortalMessage();

  const client = useRef<BrowserVoiceClient | null>(null);
  const handle = useRef<BrowserVoiceHandle | null>(null);
  const conversation = useRef<string | null>(null);
  /** Set by this page's Hang up, so an `ended { hung_up }` it did not cause reads as "ended elsewhere". */
  const endedHere = useRef(false);
  /** Bumped by every Start and by unmounting: an answer for an older attempt is dropped. */
  const attempt = useRef(0);
  const mounted = useRef(true);

  // The module, fetched when a tester chooses this test — so the click can ask for the microphone.
  useEffect(() => {
    let current = true;
    gateway.loadBrowserVoiceClient().then(
      (loaded) => {
        if (!current) return;
        client.current = loaded;
        setModuleState("ready");
      },
      () => {
        if (current) setModuleState("failed");
      },
    );
    return () => {
      current = false;
    };
  }, [gateway]);

  /** Asks the api to end the test; the runtime's end is authoritative and the call is idempotent. */
  const endOnApi = useCallback(
    (conversationId: string): Promise<void> => gateway.endVoiceTestCall(conversationId).then(() => undefined),
    [gateway],
  );

  // Leaving the page (another tab of the agent, another direction, another route) ends the test: the
  // module hangs up on `pagehide` by itself; an in-app navigation is this.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      attempt.current += 1;
      const held = handle.current;
      const conversationId = conversation.current;
      if (held !== null && isConnectedState(held.state())) {
        endedHere.current = true;
        void held.hangUp();
        if (conversationId !== null) void endOnApi(conversationId).catch(() => undefined);
      }
    };
  }, [endOnApi]);

  // The timer and the countdown. Until the first tick the timer reads 0:00 (an elapsed time is never
  // negative), which is what a conversation that has just gone live shows anyway.
  const ticking = phase.kind === "live" || (finalMinuteUntilMs !== null && isUnderWay(phase));
  useEffect(() => {
    if (!ticking) return;
    const timer = setInterval(() => {
      setNowMs(Date.now());
    }, TICK_MS);
    return () => {
      clearInterval(timer);
    };
  }, [ticking]);

  const conversationId = started?.testCall.conversationId ?? null;

  useEffect(() => {
    lineCount.current = line.length;
  }, [line]);

  // The live transcript: the same stream the phone test and the Live tab read.
  useEffect(() => {
    if (conversationId === null) return;
    const opened = gateway.streamConversationTranscript(conversationId, (frame) => {
      if (frame.event === CONFIRMATION_CODE_SHOWN_EVENT) {
        // plan-029: the code this test would have sent by text message; anything but 4 to 8 digits is not drawn.
        const code = consoleCodeOf(frame.data);
        if (code !== null) setConsoleCode(code);
        return;
      }
      if (frame.event === "customer_order_payment") {
        // Plan-005 OP-37 (IC-50): the payment element, drawn as the widget draws it; unreadable → nothing.
        const event = paymentElementEventOf(frame.data);
        if (event !== null) setPayment((held) => withPaymentEvent(held, event, lineCount.current));
        return;
      }
      if (frame.event === "conversation.event") {
        // plan-021: a pass between VoiceAgents, drawn after the lines heard before it.
        const event = voiceAgentPassFrameOf(frame.data);
        if (event !== null) setVoiceAgentPassArrival((held) => [...held, { event, afterLine: lineCount.current }]);
        return;
      }
      if (frame.event !== "turn.final" && frame.event !== "turn.partial") return;
      const data = frame.data as { speaker?: unknown; text?: unknown };
      if (typeof data.speaker !== "string" || typeof data.text !== "string") return;
      const speaker = data.speaker === "assistant" ? "assistant" : data.speaker === "caller" ? "caller" : "system";
      const text = data.text;
      setLine((held) => withFrame(held, { speaker, text, partial: frame.event === "turn.partial" }));
    });
    return () => {
      opened.close();
    };
  }, [conversationId, gateway]);

  // What was charged, read back once the conversation has ended (the runtime writes the end).
  const ended = phase.kind === "ended";
  const endedRead = useQuery({
    queryKey: ["browserTestCall", voiceAgentId, conversationId ?? ""],
    queryFn: () => gateway.readBrowserTestCall(voiceAgentId, conversationId ?? ""),
    enabled: ended && conversationId !== null,
    refetchInterval: (query) => (query.state.data?.state === "ended" ? false : ENDED_READ_INTERVAL_MS),
  });

  const start = (): void => {
    const loaded = client.current;
    if (loaded === null || isUnderWay(phase)) return;
    attempt.current += 1;
    const mine = attempt.current;
    endedHere.current = false;
    handle.current = null;
    conversation.current = null;
    setStarted(null);
    setLine([]);
    setPayment([]);
    setVoiceAgentPassArrival([]);
    setConsoleCode(null);
    setLevel(0);
    setFinalMinuteUntilMs(null);
    setResume(null);
    setHangUpNote(null);
    setPhase({ kind: "requesting_microphone" });
    // Synchronously inside the click: the browser's microphone prompt needs the gesture.
    const asking = loaded.requestMicrophone();
    void asking.then(async (microphone) => {
      if (mine !== attempt.current) {
        if (microphone.kind === "granted") stopTracks(microphone.stream);
        return;
      }
      if (microphone.kind !== "granted") {
        // AC-BV-7: no grant is asked for, and nothing is written.
        setPhase({ kind: "microphone_refused", refusal: microphone });
        return;
      }
      setPhase({ kind: "arming" });
      let answer: BrowserTestCallView;
      try {
        answer = await gateway.startBrowserTestCall(voiceAgentId, "", surveyArm);
      } catch (error: unknown) {
        stopTracks(microphone.stream);
        if (mine === attempt.current) setPhase(refusedPhaseOf(error));
        return;
      }
      const armed = answer.testCall.conversationId;
      if (mine !== attempt.current || armed === null) {
        stopTracks(microphone.stream);
        if (armed !== null) void endOnApi(armed).catch(() => undefined);
        if (mine === attempt.current) {
          setPhase({ kind: "refused", get message() { return portalText("operations", "the_test_could_not_start_nothing_was_charged_56e27f2e"); }, neutral: false, retry: false });
        }
        return;
      }
      conversation.current = armed;
      setStarted(answer);
      handle.current = loaded.connectBrowserVoice(microphone.stream, answer.grant, {
        onState: (state) => {
          if (mine !== attempt.current) return;
          setPhase(phaseOfModuleState(state, endedHere.current));
          if (state.kind === "ended" || state.kind === "failed") {
            setResume(null);
            setLevel(0);
            // The runtime ends a dropped or refused leg by itself; asking the api too costs nothing
            // (idempotent) and releases an arm that was never claimed.
            if (!endedHere.current && (state.kind === "failed" || state.reason === "connection_lost")) {
              void endOnApi(armed).catch(() => undefined);
            }
          }
        },
        onLevel: (next) => {
          if (mine === attempt.current) setLevel(next);
        },
        onFinalMinute: (remainingSecond) => {
          if (mine !== attempt.current) return;
          setFinalMinuteUntilMs(Date.now() + remainingSecond * 1000);
          setNowMs(Date.now());
        },
        onAudioBlocked: (resumePlayback) => {
          if (mine === attempt.current) setResume(() => resumePlayback);
        },
      });
    });
  };

  const hangUp = (): void => {
    const held = handle.current;
    const conversationId = conversation.current;
    endedHere.current = true;
    if (held !== null) void held.hangUp();
    if (conversationId === null) return;
    endOnApi(conversationId).catch((error: unknown) => {
      if (!mounted.current) return;
      setHangUpNote(
        () => (error instanceof Error
          ? portalText("operations", "value_the_test_ends_by_itself_within_seconds_now_that_this_1abb9aa0", { v0: String(apiErrorText(error, "")) })
          : portalText("operations", "the_api_could_not_confirm_the_end_the_test_ends_by_itself_e7096a34")),
      );
    });
  };

  const resumeAudio = (): void => {
    const pending = resume;
    if (pending === null) return;
    // Inside the click, as autoplay requires.
    void pending().then(() => {
      if (mounted.current) setResume(null);
    });
  };

  return {
    phase,
    level,
    nowMs,
    finalMinuteUntilMs,
    audioBlocked: resume !== null && isConnected(phase),
    resumeAudio,
    moduleState,
    started,
    conversationId,
    line,
    payment,
    voiceAgentPassArrival,
    consoleCode,
    endedView: endedRead.data ?? null,
    hangUpNote,
    start,
    hangUp,
  };
}

function isConnectedState(state: BrowserVoiceState): boolean {
  return state.kind === "connecting" || state.kind === "live" || state.kind === "queued" || state.kind === "reconnecting";
}

/** Why Start is not offered or waits, from the role, the availability read and the module. */
export interface StartGate {
  /** A Viewer: the reason, and no button at all. */
  readonly roleReason: string | null;
  /** The platform cannot take a browser test now (FR-BV-3): shown as a neutral panel. */
  readonly unavailableReason: string | null;
  /** Start waits, with this said beside it. */
  readonly waitReason: string | null;
}

export function useStartGate(
  availability: TestCallAvailabilityView | undefined,
  moduleState: BrowserTestCallControl["moduleState"],
): StartGate {
  const member = useMemberCan();
  const roleReason = member.whyNot("test_call.place");
  const unavailableReason =
    availability !== undefined && !availability.browserTestCallAvailable
      ? (availability.browserTestCallMessage ?? portalText("operations", "browser_tests_cannot_run_right_now_phone_tests_may_still_w_f33d985e"))
      : null;
  const waitReason =
    member.role === null
      ? portalText("operations", "checking_what_you_can_do_here_c6dd09a1")
      : availability === undefined
        ? portalText("operations", "checking_whether_browser_tests_can_run_right_now_a62d5980")
        : moduleState === "failed"
          ? portalText("operations", "the_browser_voice_part_of_the_console_could_not_be_loaded_e4bf2b83")
          : null;
  return { roleReason, unavailableReason, waitReason };
}

/** The fourteen bars of the prototype's level meter; still (and dim) until the test is live. */
export function LevelMeter({ level, active }: { readonly level: number; readonly active: boolean }): ReactNode {
  usePortalTranslation("operations");
  return (
    <span className={active ? "tc-level tc-level-active" : "tc-level"} aria-hidden="true">
      {levelBarsOf(level, active).map((scale, index) => (
        <span
          key={`bar-${String(index)}`}
          className="tc-level-bar"
          style={{ "--tc-level": String(scale) } as CSSProperties}
        />
      ))}
    </span>
  );
}

export interface BrowserVoiceControlsProps {
  readonly control: BrowserTestCallControl;
  readonly gate: StartGate;
}

/** Start / Hang up, the level meter and the timer (or what is happening), in one row. */
export function BrowserVoiceControls({ control, gate }: BrowserVoiceControlsProps): ReactNode {
  usePortalTranslation("operations");
  const { phase } = control;
  if (gate.roleReason !== null) return null;
  const underWay = isUnderWay(phase);
  const status = underWayWordsOf(phase, control.nowMs);
  const again = phase.kind === "ended" || phase.kind === "failed";
  const retry = phase.kind === "refused" && phase.retry;
  return (
    <div className="tc-actions tc-voice-row">
      {underWay ? (
        <Button
          variant="secondary"
          // Nothing to leave until the module holds the conversation.
          disabled={!isConnected(phase)}
          onClick={control.hangUp}
        >
          <Icon glyph={GLYPH.stopCircle} size="md" />
          {portalText("operations", "hang_up_66be99b5")}</Button>
      ) : (
        <Button
          variant="primary"
          disabled={
            control.moduleState !== "ready" || gate.unavailableReason !== null || gate.waitReason !== null
          }
          onClick={control.start}
        >
          <Icon glyph={GLYPH.microphone} size="md" />
          {retry ? portalText("operations", "try_again_042c862e") : again ? portalText("operations", "test_again_2c64589b") : portalText("operations", "start_voice_test_f0ae88be")}
        </Button>
      )}
      <LevelMeter level={control.level} active={phase.kind === "live"} />
      {status === null ? null : (
        <span className="tc-live" role="status">
          {phase.kind === "live" ? <span className="c-dot" aria-hidden="true" /> : null}
          {status}
        </span>
      )}
    </div>
  );
}

export interface BrowserVoiceStatusProps {
  readonly control: BrowserTestCallControl;
  readonly gate: StartGate;
}

/**
 * Everything said about the test that is not a control: why it cannot start, the refusals, the
 * microphone's fix, the countdown, "Tap to hear the agent", the ended line, the one-test rule.
 */
export function BrowserVoiceStatus({ control, gate }: BrowserVoiceStatusProps): ReactNode {
  usePortalTranslation("operations");
  const { phase, started } = control;
  const maxDurationSecond = started?.maxDurationSecond ?? 900;
  const countdown = isUnderWay(phase) ? countdownWordsOf(control.finalMinuteUntilMs, control.nowMs, maxDurationSecond) : null;
  const microphone = phase.kind === "microphone_refused" ? microphoneWordsOf(phase.refusal) : null;
  return (
    <>
      {gate.roleReason === null ? null : (
        <p className="c-note" role="status">
          {gate.roleReason} {portalText("operations", "you_can_read_what_a_browser_test_says_here_but_not_start_o_00e247fe")}</p>
      )}
      {gate.roleReason !== null || gate.unavailableReason === null ? null : (
        <div className="tc-unavailable" role="status">
          <span className="tc-unavailable-title">{portalText("operations", "browser_tests_cannot_run_right_now_cfb20780")}</span>
          <p className="tc-unavailable-dek">{gate.unavailableReason}</p>
        </div>
      )}
      {gate.roleReason !== null || gate.waitReason === null ? null : (
        <p className="c-note" role="status">
          {gate.waitReason}
        </p>
      )}
      {microphone === null ? null : (
        <div className="tc-unavailable" role="alert">
          <span className="tc-unavailable-title">{microphone.title}</span>
          <p className="tc-unavailable-dek">{microphone.fix}</p>
        </div>
      )}
      {phase.kind === "refused" ? (
        phase.neutral ? (
          <div className="tc-unavailable" role="status">
            <span className="tc-unavailable-title">{portalText("operations", "the_test_cannot_run_right_now_42a23ed1")}</span>
            <p className="tc-unavailable-dek">{phase.message}</p>
          </div>
        ) : (
          <p className="c-note c-note-critical" role="alert">
            {phase.message}
          </p>
        )
      ) : null}
      {control.audioBlocked ? (
        <div className="tc-actions">
          <Button variant="primary" onClick={control.resumeAudio}>
            <Icon glyph={GLYPH.headphones} size="md" />
            {portalText("operations", "tap_to_hear_the_agent_b80e6585")}</Button>
          <span className="tc-live">{portalText("operations", "your_browser_paused_the_agent_s_voice_until_you_tap_b8a06f8a")}</span>
        </div>
      ) : null}
      {countdown === null ? null : (
        <p className="c-note" role="timer" aria-live="polite">
          {countdown}
        </p>
      )}
      {started !== null && started.replacedConversationId !== null && isUnderWay(phase) ? (
        <p className="c-note" role="status">
          {portalText("operations", "this_test_ended_the_browser_test_you_already_had_running_o_8d96604c")}</p>
      ) : null}
      {phase.kind === "failed" ? (
        <p className="c-note" role="alert">
          {failedWordsOf(phase.reason)}
        </p>
      ) : null}
      {phase.kind === "ended" ? (
        <div className="tc-ended-block" role="status">
          <span className="tc-ended-reason">{endedWordsOf(phase.reason, phase.endedHere, maxDurationSecond)}</span>
          <span className="tc-ended">
            <Tag tone="neutral">{portalText("operations", "test_a94a8fe5")}</Tag>
            {control.endedView !== null && control.endedView.state === "ended"
              ? endedLineOf(control.endedView)
              : portalText("operations", "working_out_what_it_cost_144565a7")}
          </span>
        </div>
      ) : null}
      {control.hangUpNote === null ? null : <p className="c-note">{control.hangUpNote}</p>}
    </>
  );
}

/** The transcript pane's lines, as the phone test draws them, with the payment elements between them. */
export function TranscriptLines({
  line,
  payment,
}: {
  readonly line: readonly TranscriptLineView[];
  readonly payment: readonly PaymentElementView[];
}): ReactNode {
  usePortalTranslation("operations");
  return interleavedEntries(line, payment).map((entry) => {
    if (entry.kind === "payment") {
      return <PaymentElementCard key={`payment:${entry.payment.customerOrderPaymentId}`} element={entry.payment} />;
    }
    const one = entry.line;
    return (
      <p key={`${String(entry.index)}:${one.text.slice(0, 16)}`} className={one.partial ? "tc-turn tc-turn-partial" : "tc-turn"}>
        <span className={one.speaker === "assistant" ? "tc-who tc-who-agent" : "tc-who"}>
          {one.speaker === "assistant" ? portalText("operations", "agent_5ce2e6f4") : one.speaker === "caller" ? portalText("operations", "you_905cb326") : "—"}
        </span>
        <span>{one.text}</span>
      </p>
    );
  });
}
